import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  CheckCircle,
  Shield,
  Users,
  Settings,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import Button from "components/ui/Button";
const CompanySetupSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("pages");
  const [showConfetti, setShowConfetti] = useState(true);

  const { companyName, action } = location.state || {};

  useEffect(() => {
    // Hide confetti after 3 seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleNavigateToDashboard = () => {
    navigate("/dashboard");
  };

  const handleInviteTeam = () => {
    // Navigate to team invitation page (placeholder)
    navigate("/dashboard", { state: { openTeamInvite: true } });
  };

  const handleCompanySettings = () => {
    // Navigate to company settings page (placeholder)
    navigate("/dashboard", { state: { openCompanySettings: true } });
  };

  const isJoined = action === "joined";
  const isCreated = action === "created";
  const companyDisplayName =
    companyName ||
    t(isJoined ? "companySetup.companyFallback" : "companySetup.yourCompany");

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 50 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{ y: -10, opacity: 1 }}
              animate={{ y: window.innerHeight + 10, opacity: 0 }}
              transition={{
                duration: 3,
                delay: i * 0.1,
                ease: "easeOut",
              }}
              className="absolute w-2 h-2 bg-primary rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                backgroundColor: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"][
                  Math.floor(Math.random() * 4)
                ],
              }}
            />
          ))}
        </div>
      )}

      {/* Success Content */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.2,
              duration: 0.5,
              type: "spring",
              stiffness: 200,
            }}
            className="inline-flex items-center justify-center w-20 h-20 bg-success-100 rounded-full mb-6"
          >
            <CheckCircle className="w-10 h-10 text-success-600" />
          </motion.div>

          {/* Success Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isJoined
                ? t("companySetup.welcomeTeam")
                : t("companySetup.setupComplete")}
            </h1>
            <p className="text-gray-600 mb-6">
              {isJoined
                ? t("companySetup.joinedCompany", {
                    company: companyDisplayName,
                  })
                : t("companySetup.companyRegistered", {
                    company: companyDisplayName,
                  })}
            </p>
          </motion.div>

          {/* Company Name Display */}
          {companyName && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="bg-primary-50 rounded-lg p-4 mb-6"
            >
              <div className="flex items-center justify-center gap-2">
                <Shield className="w-5 h-5 text-primary-600" />
                <span className="font-semibold text-primary-800">
                  {companyName}
                </span>
              </div>
            </motion.div>
          )}

          {/* ZATCA Compliance Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="bg-success-50 border border-success-200 rounded-lg p-4 mb-8"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="p-2 bg-success-100 rounded-lg">
                <Shield className="w-5 h-5 text-success-600" />
              </div>
              <div className="text-start">
                <h3 className="font-medium text-success-800">
                  {t("companySetup.zatcaCompliant")}
                </h3>
                <p className="text-sm text-success-700">
                  {t("companySetup.zatcaReady")}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="space-y-3 mb-8"
          >
            <h3 className="font-medium text-gray-900 mb-4">
              {t("companySetup.next")}
            </h3>

            <div className="grid gap-3">
              <div className="flex items-center gap-3 text-start p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">
                    {t("companySetup.accessDashboard")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("companySetup.accessDashboardDescription")}
                  </p>
                </div>
              </div>

              {isCreated && (
                <div className="flex items-center gap-3 text-start p-3 bg-gray-50 rounded-lg">
                  <Users className="w-5 h-5 text-primary-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-800">
                      {t("companySetup.inviteTeamMembers")}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t("companySetup.inviteTeamDescription")}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 text-start p-3 bg-gray-50 rounded-lg">
                <Settings className="w-5 h-5 text-gray-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-800">
                    {t("companySetup.configureSettings")}
                  </p>
                  <p className="text-sm text-gray-600">
                    {t("companySetup.configureSettingsDescription")}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
            className="space-y-3"
          >
            <Button
              onClick={handleNavigateToDashboard}
              className="w-full bg-primary hover:bg-primary-600"
            >
              {t("companySetup.goToDashboard")}
              <ArrowRight className="w-4 h-4 ms-2 rtl:scale-x-[-1]" />
            </Button>

            {isCreated && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleInviteTeam}
                  className="flex-1"
                >
                  <Users className="w-4 h-4 me-2" />
                  {t("companySetup.inviteTeam")}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCompanySettings}
                  className="flex-1"
                >
                  <Settings className="w-4 h-4 me-2" />
                  {t("companySetup.settings")}
                </Button>
              </div>
            )}
          </motion.div>
        </div>

        {/* Bottom Trust Indicator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mt-6 text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-gray-600">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>{t("companySetup.poweredBy")}</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default CompanySetupSuccess;


